import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { useFocusTrap } from '../../shared/focus_trap.js';

/** Devolve o foco a quem abriu. `to` é lido na hora de fechar, não na abertura:
 *  vale o gatilho que a ref aponta AGORA, e `opener` só quando ela está vazia. */
function returnFocus(to: RefObject<HTMLElement | null>, opener: Element | null): void {
  const target = to.current ?? opener;
  if (target instanceof HTMLElement) target.focus();
}

/**
 * **A escolha de poltrona em tela cheia no celular e no tablet** — a MESMA
 * casca da tela de tarifas do voo no celular (`FaresPageTakeover`, genui-kit):
 * cabeçalho com voltar, título e a linha da viagem, a planta rolando com a
 * página inteira e o `Selecionar` na faixa pregada no pé. Antes abria numa
 * folha de baixo (`PageFieldSheet`), a casca dos campos da barra de busca.
 *
 * MODAL de verdade: portal no `body` (com a classe `.genui2`, porque as
 * custom properties do DS só herdam por ancestral), `aria-modal`, trap de foco
 * e Esc. Fechar não perde nada: a escolha fica no host, e o card segue na
 * lista. O foco entra no painel (o leitor de tela anuncia o título) e, ao
 * fechar, volta para quem abriu (`returnFocusTo`, o `Selecionar` do card).
 */
export function BusSeatTakeover({
  title,
  subtitle,
  backLabel,
  returnFocusTo,
  onClose,
  children,
}: Readonly<{
  /** "Escolha sua poltrona". */
  title: string;
  /** A linha da viagem ("Ida · Qui, 22 out · 1001"). */
  subtitle: string;
  /** O nome acessível do voltar ("Fechar escolha de poltrona"). */
  backLabel: string;
  /** Quem abriu a tela — o `Selecionar` do card. Sem ele, o foco volta ao
   *  elemento ativo de quando a tela abriu. */
  returnFocusTo: RefObject<HTMLElement | null>;
  onClose: () => void;
  children: ReactNode;
}>): ReactNode {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(panelRef, true);

  // O foco entra uma vez e volta ao desmontar (`returnFocusTo` é uma ref:
  // estável, não reabre o efeito).
  useEffect(() => {
    const opener = document.activeElement;
    panelRef.current?.focus();
    return () => {
      returnFocus(returnFocusTo, opener);
    };
  }, [returnFocusTo]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // Um diálogo que porta para FORA deste painel cuida do próprio Esc.
      const dialog =
        event.target instanceof Element ? event.target.closest('[role="dialog"]') : null;
      if (dialog && dialog !== panelRef.current && !panelRef.current?.contains(dialog)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div className="genui2">
      <div
        ref={panelRef}
        // `z-50`: a régua da tela de tarifas do voo. O `pt` soma a status bar
        // do iPhone (viewport-fit=cover no WebView).
        className="fixed inset-0 z-50 flex flex-col gap-4 overflow-y-auto overscroll-contain bg-background scrollbar-rail pr-[calc(1rem-var(--scrollbar-rail))] pl-4 pt-[calc(12px+env(safe-area-inset-top,0px))] outline-none"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="-ml-1 shrink-0"
            aria-label={backLabel}
            onClick={onClose}
          >
            <ArrowLeftIcon className="size-6" aria-hidden />
          </Button>
          <div className="grid min-w-0">
            <h1 id={titleId} className="m-0 truncate text-heading text-foreground">
              {title}
            </h1>
            <p className="m-0 truncate text-detail font-medium text-muted-foreground">{subtitle}</p>
          </div>
        </header>
        {children}
      </div>
    </div>,
    document.body,
  );
}
